/**
 * App Layout
 * Main application layout with collapsible sidebar navigation
 * Uses the shared @jourfx/ui sidebar components
 */
import { Outlet, Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Calendar,
  Settings,
  LogOut,
  Bell,
  ChevronsUpDown,
  User,
  BadgeCheck,
  CreditCard,
  Sun,
  Moon,
  Monitor,
  Clock,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  Separator,
} from "@jourfx/ui";
import { useAuth } from "@/hooks/useAuth";
import { signOut } from "@/lib/auth";
import { useTheme } from "@/components/ThemeProvider";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";

type InternalRole = "gutachter" | "assistent";

const getNavigation = (t: (key: string) => string, role?: string | null) => {
  const items = [
    {
      name: t("common:nav.dashboard"),
      href: "/dashboard",
      icon: LayoutDashboard,
      roles: ["gutachter"] as InternalRole[],
    },
    {
      name: t("common:nav.appointments"),
      href: "/appointments",
      icon: Calendar,
      roles: ["gutachter", "assistent"] as InternalRole[],
    },
    {
      name: t("common:nav.calendar"),
      href: "/calendar",
      icon: Calendar,
      roles: ["gutachter", "assistent"] as InternalRole[],
    },
    {
      name: t("common:nav.availability"),
      href: "/availability",
      icon: Clock,
      roles: ["gutachter", "assistent"] as InternalRole[],
    },
    {
      name: t("common:nav.versicherte"),
      href: "/versicherte",
      icon: User,
      roles: ["gutachter", "assistent"] as InternalRole[],
    },
  ];

  if (!role) return items;
  return items.filter((item) => item.roles.includes(role as InternalRole));
};

const getSettingsNav = (t: (key: string) => string) => [
  { name: t("common:nav.settings"), href: "/settings", icon: Settings },
];

export function AppLayout() {
  const { t } = useTranslation("common");
  const location = useLocation();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  const squareLogoUrl = useBrandLogoUrl("square");
  const navigation = getNavigation(t, user?.role || null);
  const settingsNav = getSettingsNav(t);

  const handleLogout = async () => {
    await signOut();
    window.location.href = "/login";
  };

  // Note: No loading spinner here - _authenticated.tsx already guards auth.
  // Showing a spinner here would unmount the entire sidebar on navigation,
  // causing a visual "full page reload" effect.

  const userName = user?.name || t("user.defaultName");
  const userEmail = user?.email || t("user.noEmail");
  const userRole = user?.role || "benutzer";
  const userInitials = userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/dashboard">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg border bg-white overflow-hidden">
                    <img
                      src={squareLogoUrl}
                      alt="Logo"
                      className="h-6 w-6 object-contain"
                    />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">JourFx</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.organization?.name || "Video-Begutachtung"}
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>{t("nav.navigation")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                        <Link to={item.href as any}>
                          <item.icon />
                          <span>{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>{t("nav.system")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {settingsNav.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                        <Link to={item.href as any}>
                          <item.icon />
                          <span>{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{userName}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {userEmail}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                  side="top"
                  align="start"
                  sideOffset={4}
                >
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{userName}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {userEmail}
                      </span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                    <BadgeCheck className="mr-2 h-4 w-4" />
                    <span className="capitalize">{userRole}</span>
                    {user?.license && (
                      <Badge variant="outline" className="ml-auto text-xs capitalize">
                        {user.license.tier}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <User className="mr-2 h-4 w-4" />
                      {t("user.profile")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <Settings className="mr-2 h-4 w-4" />
                      {t("user.settings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <div className="flex items-center rounded-md bg-muted p-1">
                      <button
                        onClick={() => setTheme("light")}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors ${
                          theme === "light"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Sun className="h-3.5 w-3.5" />
                        {t("theme.light")}
                      </button>
                      <button
                        onClick={() => setTheme("dark")}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors ${
                          theme === "dark"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Moon className="h-3.5 w-3.5" />
                        {t("theme.dark")}
                      </button>
                      <button
                        onClick={() => setTheme("system")}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors ${
                          theme === "system"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Monitor className="h-3.5 w-3.5" />
                        {t("theme.system")}
                      </button>
                    </div>
                  </div>
                  {user?.organization && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem disabled>
                        <CreditCard className="mr-2 h-4 w-4" />
                        {user.organization.name}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("user.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" data-testid="sidebar-trigger" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="flex-1" />
          <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground h-9 w-9">
            <Bell className="h-5 w-5" />
          </button>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
